import type {
  Tasks,
  StepConfig,
  Workflow,
  WorkflowData,
} from '../../../workflow/schemaTypes';
import { useWorkflow } from '../../../workflow/hooks';
import { isTaskComplete } from '../../../utils/tasks';
import { type LanguageCore, Text } from '../../Language/Language';
import s from './TaskPreview.module.css';
import { clsx } from 'clsx';

export type TaskPreviewProps = {
  itemId: string;
  group: {
    id: string;
    scope: string;
    mode: string;
    tasks: Array<{ id: Tasks }>;
  };
};

const taskLangKeyMap: Partial<Record<Tasks, string>> = {
  reason: 'Global.reasons.reasonSelection.Header',
  resolution: 'Global.resolutions.resolutionSelection.Header',
};

const getNextPreviewTaskId = (
  currentStep: StepConfig | null | undefined,
  workflowConfig: Workflow | null,
  group: TaskPreviewProps['group'],
  data: WorkflowData | null,
  itemId: string,
): Tasks | null => {
  if (!currentStep || !workflowConfig) return null;

  // Get full task configs for the tasks in this group
  const fullTasks = currentStep.tasks.filter((task) =>
    group.tasks.some(({ id }) => id === task.id),
  );

  // Only consider enabled, item-scoped tasks
  const enabledItemTasks = fullTasks.filter(
    (task) => task.scope === 'item' && task.mode === 'enabled',
  );

  if (enabledItemTasks.length === 0) return null;

  // Find the first incomplete task for this item
  const currentTask = enabledItemTasks.find(
    (task) => !isTaskComplete(task.id, data, workflowConfig, itemId),
  );

  if (!currentTask) return null; // all complete

  // Get the task that comes after the current incomplete task
  const currentTaskIndex = enabledItemTasks.indexOf(currentTask);
  const nextTask = enabledItemTasks[currentTaskIndex + 1];
  return nextTask?.id ?? null;
};

export const TaskHeader = ({
  disabled,
  id,
  language,
  lastItem,
}: {
  disabled?: boolean;
  id?: string;
  language?: LanguageCore;
  lastItem?: boolean;
}) => (
  <div
    className={clsx(
      s.container,
      disabled && s.disabled,
      lastItem && s.lastItem,
    )}
    aria-disabled={disabled}
    tabIndex={-1}
  >
    <h2 id={id} className={s.title}>
      <Text {...language} />
    </h2>
  </div>
);

export const TaskPreview = ({ itemId, group }: TaskPreviewProps) => {
  const { data, config: workflowConfig, currentStep } = useWorkflow();

  const nextPreviewTaskId = getNextPreviewTaskId(
    currentStep,
    workflowConfig,
    group,
    data,
    itemId,
  );

  if (!nextPreviewTaskId) {
    return null;
  }

  const langKey = taskLangKeyMap[nextPreviewTaskId];

  if (!langKey) {
    return null;
  }

  return (
    <TaskHeader
      id={`${nextPreviewTaskId}TitlePreview-${itemId}`}
      language={{ langKey }}
      disabled
      lastItem
    />
  );
};
